import * as OTPAuth from 'otpauth'
import { getCorrectedNow } from './timeSync'

// 비표준 TOTP(Steam)는 출력 알파벳/인코딩이 표준과 달라 표준 생성으로 올바른 코드가 나오지 않는다 → 미지원.
// issuer 정확 일치로 판별("Steamworks"/"my-steam-account" 등 정상 서비스 오탐 방지).
function assertStandard(issuer) {
  if ((issuer || '').trim().toLowerCase() === 'steam') {
    const e = new Error('UNSUPPORTED_TOTP')
    e.code = 'UNSUPPORTED_TOTP'
    throw e
  }
}

function totpError(code, cause) {
  const e = new Error(code)
  e.code = code
  if (cause) e.cause = cause
  return e
}

function parseTotp(otpauthUri) {
  const otp = OTPAuth.URI.parse(otpauthUri)
  if (!(otp instanceof OTPAuth.TOTP)) throw totpError('UNSUPPORTED_TOTP')
  assertStandard(otp.issuer)
  return otp
}

// base32 secret 정제 + 검증. Secret.fromBase32는 빈 문자열/1글자(5비트<1바이트)를 throw 없이
// "0바이트 secret"으로 만들어(실측) 빈 열쇠 TOTP가 조용히 저장될 수 있다 → 여기서 차단.
function toSecret(rawSecret) {
  const cleaned = (rawSecret || '').replace(/\s+/g, '').replace(/=+$/, '').toUpperCase()
  if (!cleaned) throw totpError('INVALID_TOTP_INPUT')
  const secret = OTPAuth.Secret.fromBase32(cleaned)
  if (secret.buffer.byteLength === 0) throw totpError('INVALID_TOTP_INPUT')
  return secret
}

// bare base32 secret 또는 otpauth:// URI를 정규 otpauth:// URI로 변환한다.
// 저장은 항상 이 정규 URI 한 형태로 통일되어, 이후 소비자는 입력 경로를 알 필요가 없다.
export function normalizeToOtpauthUri(input, { issuer = '', account = '' } = {}) {
  const raw = (input || '').trim()
  if (!raw) throw totpError('INVALID_TOTP_INPUT')

  try {
    if (raw.toLowerCase().startsWith('otpauth://')) {
      return parseTotp(raw).toString()
    }
    // bare secret은 항상 표준 TOTP(Steam 비표준은 otpauth URI로만 전달됨). issuer/label은 사용자 메타일 뿐이므로
    // 여기서 assertStandard를 호출하지 않는다 — 라벨이 "steam"이어도 정상 저장돼야 하고, validator(issuer 없음)와
    // 저장(issuer=label) 경로의 throw 불일치를 방지. Steam 거부는 parseTotp(실제 URI의 issuer)에서만 수행.
    const totp = new OTPAuth.TOTP({
      issuer,
      label: account || issuer || 'account',
      secret: toSecret(raw),
    })
    return totp.toString()
  } catch (err) {
    if (err && err.code) throw err
    throw totpError('INVALID_TOTP_INPUT', err)
  }
}

// otpauth URI를 1회만 파싱해 (now) => {code, secondsRemaining, period} 생성기를 반환한다.
// 뷰가 매 tick마다 URI를 재파싱하지 않도록 하기 위함.
export function createGenerator(otpauthUri) {
  const otp = parseTotp(otpauthUri)
  const { period } = otp
  return (now = getCorrectedNow()) => ({
    code: otp.generate({ timestamp: now }),
    secondsRemaining: period - (Math.floor(now / 1000) % period),
    period,
  })
}

// 저장된 otpauth URI에서 base32 secret만 꺼낸다(편집 화면 표시용).
// 파싱 실패/비표준은 그대로 throw — 호출부(TotpFieldEdit)가 catch해 raw 표시로 폴백한다.
export function extractSecret(otpauthUri) {
  return parseTotp(otpauthUri).secret.base32
}

// 원본 URI의 파라미터(issuer/label/algorithm/digits/period)는 보존하고 secret만 교체한 URI를 만든다.
// bare 재정규화는 파라미터를 기본값(SHA1/6/30)으로 리셋해 비표준 TOTP를 조용히 깨뜨리므로 이 함수를 쓴다.
export function replaceSecret(otpauthUri, newSecret) {
  const src = parseTotp(otpauthUri)
  try {
    const totp = new OTPAuth.TOTP({
      issuer: src.issuer,
      label: src.label,
      algorithm: src.algorithm,
      digits: src.digits,
      period: src.period,
      secret: toSecret(newSecret),
    })
    return totp.toString()
  } catch (err) {
    if (err && err.code) throw err
    throw totpError('INVALID_TOTP_INPUT', err)
  }
}

// 실서비스 secret은 대개 16자 이상(GitHub 16자) — 이보다 짧으면 오타/부분 복사 가능성이 높아
// 저장 확인 팝업에 경고 한 줄을 덧붙인다(차단 아님 — 짧은 secret도 규격상 유효). PR5.5.
export const SHORT_TOTP_SECRET_LEN = 16

// 저장 확인 팝업의 "짧은 키" 경고 판정용 secret 길이. 빈/null·파싱 불가면 null(판단 불가 시 경고 안 함).
// bare는 base32 유효성 무관 공백 제거 길이만 반환 — 유효성 차단은 폼 validator 책임(설계 §테스트 #3).
export function getTotpSecretLength(formValue) {
  if (!formValue) return null
  const raw = String(formValue).trim()
  if (!raw) return null
  if (raw.toLowerCase().startsWith('otpauth://')) {
    try {
      return extractSecret(raw).length
    } catch (err) {
      return null
    }
  }
  return raw.replace(/\s+/g, '').length
}

// 단발성 코드 계산(편집 미리보기 등). 반복 표시에는 createGenerator를 사용할 것.
export function generateCode(otpauthUri, now = getCorrectedNow()) {
  try {
    return createGenerator(otpauthUri)(now)
  } catch (err) {
    if (err && err.code) throw err
    throw totpError('INVALID_TOTP_INPUT', err)
  }
}
